import { assertNever } from "@msys/common";
import type {
  DefineItemProductSearchFilterDefinitionsInput,
  ItemTemplateSearchFilterPropertyFilterInput,
  ProductSearchFilterPropertyFilterInput,
  ItemProductSearchFilterBrandFilterInput,
  ItemProductSearchFilterFavouritesFilterInput,
  ItemProductSearchFilterOrganisationIdFilterInput,
  ItemProductSearchFilterPriceFilterInput,
  ItemProductSearchFilterPropertyIdFilterInput,
} from "../../../clients/graphqlTypes.js";
import type { ItemProductSearchFilterDefinitionsFragment } from "../products/productSearchFilters.generated.js";
import type { SearchFilterValueOptionsShape } from "./searchFilterSlots.js";
import {
  getValueWithOperator,
  isComputedSearchFilterSlot,
  searchFilterInputKey,
  searchFilterSlotHasValue,
  searchFilterSlotToInput,
} from "./searchFilterSlots.js";

/** The product context's named filters — the ones addressed by field, not key. */
type NamedProductFilter = NonNullable<
  | ItemProductSearchFilterDefinitionsFragment["listPriceFilter"]
  | ItemProductSearchFilterDefinitionsFragment["brandFilter"]
  | ItemProductSearchFilterDefinitionsFragment["brandLineFilter"]
  | ItemProductSearchFilterDefinitionsFragment["favouritesFilter"]
>;

/** The product filter write input, minus the addressing fields. */
export type ProductSearchFiltersInput = Omit<
  DefineItemProductSearchFilterDefinitionsInput,
  "projectId" | "docId" | "itemId"
>;

/**
 * Round-trip the stored product filters back to write input. The merged
 * mutation replaces the whole set, so every edit re-sends the filters it does
 * not touch — including the expression-driven ones, whose `*Computed`
 * variants carry their source rather than a value.
 *
 * `netPriceFilter` is read-only: it has no slot in storage and no write
 * variant, so it is not round-tripped.
 */
export function productSearchFiltersToInput(
  definitions: ItemProductSearchFilterDefinitionsFragment
): ProductSearchFiltersInput {
  return {
    propertyFilters: definitions.propertyFilters.map(searchFilterSlotToInput),
    listPriceFilter: priceFilterToInput(definitions.listPriceFilter),
    brandFilter: textFilterToInput(definitions.brandFilter),
    brandLineFilter: textFilterToInput(definitions.brandLineFilter),
    favouritesFilter: favouritesFilterToInput(definitions.favouritesFilter),
    supplierIdFilter: supplierIdFilterToInput(definitions.supplierIdFilter),
    productTypeIdFilter: productTypeIdFilterToInput(
      definitions.productTypeIdFilter
    ),
  };
}

/**
 * The named filters as they survive a product-type change: the authored ones
 * round-tripped, the expression-driven ones dropped. A product-type change
 * clears every expression-driven slot — the expression was authored against
 * the type being replaced — while the authored named filters (brand, brand
 * line, list price, supplier, favourites) carry over.
 */
export function authoredNamedProductFiltersToInput(
  definitions: ItemProductSearchFilterDefinitionsFragment
): Pick<
  ProductSearchFiltersInput,
  | "listPriceFilter"
  | "brandFilter"
  | "brandLineFilter"
  | "favouritesFilter"
  | "supplierIdFilter"
> {
  return {
    listPriceFilter: priceFilterToInput(authored(definitions.listPriceFilter)),
    brandFilter: textFilterToInput(authored(definitions.brandFilter)),
    brandLineFilter: textFilterToInput(authored(definitions.brandLineFilter)),
    favouritesFilter: favouritesFilterToInput(
      authored(definitions.favouritesFilter)
    ),
    // The Id family has no computed counterpart, so it is always authored.
    supplierIdFilter: supplierIdFilterToInput(definitions.supplierIdFilter),
  };
}

function authored<T extends { __typename: string }>(
  filter: T | null | undefined
): T | null {
  return filter && !filter.__typename.endsWith("Computed") ? filter : null;
}

/**
 * Fold the product-search form's output into the stored filter set.
 *
 * The form resolves every filter to a plain value, so writing it wholesale
 * through the merged mutation would rewrite an expression-driven slot as
 * authored — or drop it where the expression produced no result — removing a
 * formula the user never touched. Expression-driven slots are therefore
 * re-emitted from storage and the form's value for those keys is ignored; so
 * are the authored slots the form cannot represent — the valueless ones an
 * expression removal leaves behind, named and property alike.
 */
export function mergeProductFilterFormInput(
  formInput: ProductSearchFiltersInput,
  definitions: ItemProductSearchFilterDefinitionsFragment
): ProductSearchFiltersInput {
  const preservedKeys = new Set(
    definitions.propertyFilters
      .filter(
        slot =>
          isComputedSearchFilterSlot(slot) || !searchFilterSlotHasValue(slot)
      )
      .map(slot => slot.key)
  );

  // Precedence per named filter: a formula owns its slot outright; otherwise
  // the form's value wins, since the form is where an authored filter is
  // edited; and where the form has none, a stored valueless slot is re-emitted
  // so that a filter left empty by an expression removal is not dropped by an
  // unrelated save.
  return {
    ...formInput,
    listPriceFilter:
      priceFilterToInput(computed(definitions.listPriceFilter)) ??
      formInput.listPriceFilter ??
      priceFilterToInput(authoredValueless(definitions.listPriceFilter)),
    brandFilter:
      textFilterToInput(computed(definitions.brandFilter)) ??
      formInput.brandFilter ??
      textFilterToInput(authoredValueless(definitions.brandFilter)),
    brandLineFilter:
      textFilterToInput(computed(definitions.brandLineFilter)) ??
      formInput.brandLineFilter ??
      textFilterToInput(authoredValueless(definitions.brandLineFilter)),
    favouritesFilter:
      favouritesFilterToInput(computed(definitions.favouritesFilter)) ??
      formInput.favouritesFilter ??
      favouritesFilterToInput(authoredValueless(definitions.favouritesFilter)),
    propertyFilters: [
      ...formInput.propertyFilters.filter(entry => {
        const key = searchFilterInputKey(entry);
        return key === undefined || !preservedKeys.has(key);
      }),
      ...definitions.propertyFilters
        .filter(slot => preservedKeys.has(slot.key))
        .map(searchFilterSlotToInput),
    ],
  };
}

function computed<T extends { __typename: string }>(
  filter: T | null | undefined
): T | null {
  return filter && filter.__typename.endsWith("Computed") ? filter : null;
}

/**
 * A stored named filter that is authored and holds no value — what removing an
 * expression that never computed leaves behind. Narrowed to the valueless case
 * deliberately: a stored filter that *has* a value is represented in the form,
 * so letting it win there would override the edit the form exists to make.
 */
function authoredValueless<T extends NamedProductFilter>(
  filter: T | null | undefined
): T | null {
  const slot = authored(filter);
  return slot && getValueWithOperator(slot) === "-" ? slot : null;
}

function priceFilterToInput(
  filter:
    | NonNullable<ItemProductSearchFilterDefinitionsFragment["listPriceFilter"]>
    | null
    | undefined
): ItemProductSearchFilterPriceFilterInput | null {
  if (!filter) return null;
  switch (filter.__typename) {
    case "EntitySearchNumberFilter":
      return {
        numberFilter: {
          operatorNumber: filter.operatorNumber,
          valueNumber: filter.valueNumber,
        },
      };
    case "EntitySearchNumberFilterComputed":
      return {
        numberFilterComputed: {
          operatorNumber: filter.operatorNumber,
          expr: filter.expr,
        },
      };
    case "EntitySearchNumberBetweenFilter":
      return {
        numberBetweenFilter: {
          operatorNumberBetween: filter.operatorNumberBetween,
          valueNumberBetween: filter.valueNumberBetween,
        },
      };
    case "EntitySearchNumberBetweenFilterComputed":
      return {
        numberBetweenFilterComputed: {
          operatorNumberBetween: filter.operatorNumberBetween,
          expr: filter.expr,
        },
      };
    default:
      return assertNever(filter);
  }
}

function textFilterToInput(
  filter:
    | NonNullable<ItemProductSearchFilterDefinitionsFragment["brandFilter"]>
    | null
    | undefined
): ItemProductSearchFilterBrandFilterInput | null {
  if (!filter) return null;
  switch (filter.__typename) {
    case "EntitySearchTextFilter":
      return {
        textFilter: {
          operatorText: filter.operatorText,
          valueText: filter.valueText,
        },
      };
    case "EntitySearchTextFilterComputed":
      return {
        textFilterComputed: {
          operatorText: filter.operatorText,
          expr: filter.expr,
        },
      };
    case "EntitySearchTextInFilter":
      return {
        textInFilter: {
          operatorTextIn: filter.operatorTextIn,
          valueTextIn: filter.valueTextIn,
        },
      };
    case "EntitySearchTextInFilterComputed":
      return {
        textInFilterComputed: {
          operatorTextIn: filter.operatorTextIn,
          expr: filter.expr,
        },
      };
    default:
      return assertNever(filter);
  }
}

function favouritesFilterToInput(
  filter:
    | NonNullable<
        ItemProductSearchFilterDefinitionsFragment["favouritesFilter"]
      >
    | null
    | undefined
): ItemProductSearchFilterFavouritesFilterInput | null {
  if (!filter) return null;
  switch (filter.__typename) {
    case "EntitySearchBoolFilter":
      return {
        boolFilter: {
          operatorBool: filter.operatorBool,
          valueBool: filter.valueBool,
        },
      };
    case "EntitySearchBoolFilterComputed":
      return {
        boolFilterComputed: {
          operatorBool: filter.operatorBool,
          expr: filter.expr,
        },
      };
    default:
      return assertNever(filter);
  }
}

function supplierIdFilterToInput(
  filter:
    | NonNullable<
        ItemProductSearchFilterDefinitionsFragment["supplierIdFilter"]
      >
    | null
    | undefined
): ItemProductSearchFilterOrganisationIdFilterInput | null {
  if (!filter) return null;
  switch (filter.__typename) {
    case "EntitySearchIdFilter":
      return {
        idFilter: { operatorId: filter.operatorId, valueId: filter.valueId },
      };
    case "EntitySearchIdInFilter":
      return {
        idInFilter: {
          operatorIdIn: filter.operatorIdIn,
          valueIdIn: filter.valueIdIn,
        },
      };
    default:
      return assertNever(filter);
  }
}

function productTypeIdFilterToInput(
  filter:
    | NonNullable<
        ItemProductSearchFilterDefinitionsFragment["productTypeIdFilter"]
      >
    | null
    | undefined
): ItemProductSearchFilterPropertyIdFilterInput | null {
  // The product type is an Id filter, so it has exactly one clause and no
  // computed counterpart.
  return filter
    ? { idFilter: { operatorId: filter.operatorId, valueId: filter.valueId } }
    : null;
}

/**
 * The authored product property filters as product-search form values. Rows
 * that are expression-driven, or authored but valueless, contribute nothing:
 * the search form's clause inputs require a value.
 */
export function productSearchFilterPropertyFiltersToFormValues(
  propertyFilters: ItemProductSearchFilterDefinitionsFragment["propertyFilters"]
): ProductSearchFilterPropertyFilterInput[] {
  return propertyFilters.flatMap<ProductSearchFilterPropertyFilterInput>(
    propertyFilter => {
      switch (propertyFilter.__typename) {
        case "EntitySearchPropertyFilterBoolFilter": {
          const value = propertyFilter.valueBool;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              boolFilter: {
                operatorBool: propertyFilter.operatorBool,
                valueBool: value,
              },
            },
          ];
        }
        case "EntitySearchPropertyFilterBoolInFilter": {
          const value = propertyFilter.valueBoolIn;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              boolInFilter: {
                operatorBoolIn: propertyFilter.operatorBoolIn,
                valueBoolIn: value,
              },
            },
          ];
        }
        case "EntitySearchPropertyFilterNumberFilter": {
          const value = propertyFilter.valueNumber;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              numberFilter: {
                operatorNumber: propertyFilter.operatorNumber,
                valueNumber: value,
              },
            },
          ];
        }
        case "EntitySearchPropertyFilterNumberInFilter": {
          const value = propertyFilter.valueNumberIn;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              numberInFilter: {
                operatorNumberIn: propertyFilter.operatorNumberIn,
                valueNumberIn: value,
              },
            },
          ];
        }
        case "EntitySearchPropertyFilterNumberBetweenFilter": {
          const value = propertyFilter.valueNumberBetween;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              numberBetweenFilter: {
                operatorNumberBetween: propertyFilter.operatorNumberBetween,
                valueNumberBetween: value,
              },
            },
          ];
        }
        case "EntitySearchPropertyFilterTextFilter": {
          const value = propertyFilter.valueText;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              textFilter: {
                operatorText: propertyFilter.operatorText,
                valueText: value,
              },
            },
          ];
        }
        case "EntitySearchPropertyFilterTextInFilter": {
          const value = propertyFilter.valueTextIn;
          if (value === null || value === undefined) return [];
          return [
            {
              key: propertyFilter.key,
              textInFilter: {
                operatorTextIn: propertyFilter.operatorTextIn,
                valueTextIn: value,
              },
            },
          ];
        }
        default:
          return [];
      }
    }
  );
}

/**
 * Detach the expression from a named product filter, keeping it at the value
 * the expression last produced — `null` where it produced none. The bare
 * counterpart of `searchFilterSlotWithoutExpression`.
 */
export function bareTextFilterWithoutExpression(
  filter: NonNullable<ItemProductSearchFilterDefinitionsFragment["brandFilter"]>
): ItemProductSearchFilterBrandFilterInput {
  switch (filter.__typename) {
    case "EntitySearchTextFilterComputed":
      return {
        textFilter: {
          operatorText: filter.operatorText,
          valueText: filter.missingValue ? null : filter.valueTextComputed,
        },
      };
    case "EntitySearchTextInFilterComputed":
      return {
        textInFilter: {
          operatorTextIn: filter.operatorTextIn,
          valueTextIn: filter.missingValue ? null : filter.valueTextInComputed,
        },
      };
    default:
      return textFilterToInput(filter) ?? {};
  }
}

/** The list-price twin of {@link bareTextFilterWithoutExpression}. */
export function barePriceFilterWithoutExpression(
  filter: NonNullable<
    ItemProductSearchFilterDefinitionsFragment["listPriceFilter"]
  >
): ItemProductSearchFilterPriceFilterInput {
  switch (filter.__typename) {
    case "EntitySearchNumberFilterComputed":
      return {
        numberFilter: {
          operatorNumber: filter.operatorNumber,
          valueNumber: filter.missingValue ? null : filter.valueNumberComputed,
        },
      };
    case "EntitySearchNumberBetweenFilterComputed":
      return {
        numberBetweenFilter: {
          operatorNumberBetween: filter.operatorNumberBetween,
          valueNumberBetween: filter.missingValue
            ? null
            : filter.valueNumberBetweenComputed,
        },
      };
    default:
      return priceFilterToInput(filter) ?? {};
  }
}

/**
 * The row's value options taken from a PIM property type's own value list —
 * its full set — rather than the values that happen to occur among currently
 * matching products, which is what the product filter modal's availability
 * search offers. A property type with no value list is free-form.
 */
export function searchFilterValueOptionsFromPimPropertyType(
  productTypePropertyType:
    | {
        propertyType: { kind: string; unit?: string | null };
        propertyTypeValues: Array<{ key: string; label: string }>;
      }
    | undefined
): SearchFilterValueOptionsShape {
  if (!productTypePropertyType) return { kind: "text", options: [] };

  const { propertyType, propertyTypeValues } = productTypePropertyType;
  const options = propertyTypeValues.map(v => ({
    value: v.key,
    label: v.label,
  }));

  switch (propertyType.kind) {
    case "bool":
      return { kind: "bool" };
    case "number":
      return { kind: "number", unit: propertyType.unit, options };
    default:
      return { kind: "text", options };
  }
}

/** A fresh authored product property filter, valueless until the row sets one. */
export function newAuthoredProductFilterForPropertyType(propertyType: {
  key: string;
  kind: string;
}): ItemTemplateSearchFilterPropertyFilterInput {
  switch (propertyType.kind) {
    case "bool":
      return {
        boolFilter: {
          key: propertyType.key,
          operatorBool: "eq",
          valueBool: null,
        },
      };
    case "number":
      return {
        numberInFilter: {
          key: propertyType.key,
          operatorNumberIn: "in",
          valueNumberIn: null,
        },
      };
    default:
      return {
        textInFilter: {
          key: propertyType.key,
          operatorTextIn: "in",
          valueTextIn: null,
        },
      };
  }
}

/**
 * The filter scope for the named filters' availability facet: the placeholder's
 * product type and nothing else.
 *
 * Deliberately not the live filter set. The facet answers "what could this
 * filter be set to", so narrowing it by the filters already applied would hide
 * the values the user is choosing between — picking one brand would empty the
 * brand list. The product-search modal narrows by the whole set because it is
 * answering a different question: "what will I get".
 */
export function productFilterFacetScope(productTypeId: string | undefined) {
  return { productTypeIds: productTypeId ? [productTypeId] : [] };
}
